import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "./styles.scss";

interface FancyPLData {
  user_id: string;
  user_name: string;
  sport_name: string;
  match_id: string;
  match_name: string;
  market_id: string;
  market_name: string;
  winner_name: string;
  settle_date_time: string;
  casinoProvider: string | null;
  qtRoundId: string | null;
  qtClientRoundId: string | null;
  auraMarketId: string | null;
  auraRountId: string | null;
  total: number;
  commission: number;
  result_date: string;
  user_pl: number;
  event_name: string;
  event_id: string;
  type: string;
  is_back: string;
  odds: string;
  stack: string;
  chips: number;
  createdAt: string;
  match_date: string;
  p_l_full: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  onClickRow: (event_id: string, type: string) => void;
}

const formatDateTime = (dateString?: string) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const FancyPLMarketWiseTableEventTable: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  onClickRow,
}) => {
  // State for sorting
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Handle column sorting
  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  // Get sorting class for the column
  const getSortClass = (key: keyof FancyPLData) => {
    const isActive = sortKey === key;
    const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
    const downClass =
      isActive && sortOrder === "desc" ? "text-dark" : "text-muted";
    return (
      <span className="d-flex flex-column ms-2">
        <i className={`bi bi-caret-up-fill ${upClass}`} style={{ lineHeight: 0.6 }} />
        <i className={`bi bi-caret-down-fill ${downClass}`} style={{ lineHeight: 0.6 }} />
      </span>
    );
  };

  // Sort the data based on the sort key and order
  const sortedData = sortKey
    ? [...fancyPLData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        // Sorting for string values
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Sorting for numeric values
        const aNum =
          typeof aVal === "number"
            ? aVal
            : parseFloat(aVal as string) || 0; // Fix parsing error for non-numeric strings
        const bNum =
          typeof bVal === "number"
            ? bVal
            : parseFloat(bVal as string) || 0; // Fix parsing error for non-numeric strings

        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      })
    : fancyPLData;

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th
              onClick={() => handleSort("sport_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name")}
              </div>
            </th>
            <th
              onClick={() => handleSort("event_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("event_name")}
              </div>
            </th>
            <th
              onClick={() => handleSort("market_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Market Name</span>
                {getSortClass("market_name")}
              </div>
            </th>
            <th
              onClick={() => handleSort("winner_name")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Result</span>
                {getSortClass("winner_name")}
              </div>
            </th>
            <th
              onClick={() => handleSort("p_l_full")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Profite/Loss</span>
                {getSortClass("p_l_full")}
              </div>
            </th>
            <th
              onClick={() => handleSort("commission")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Commission</span>
                {getSortClass("commission")}
              </div>
            </th>
            <th
              onClick={() => handleSort("settle_date_time")}
              style={{ cursor: "pointer" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <span>Settle Time</span>
                {getSortClass("settle_date_time")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={index}
              onClick={() =>
                onClickRow(String(item.event_id), String(item.type))
              }
              style={{ cursor: "pointer" }}
            >
              <td>{(item.sport_name).replace(/\(\d+\)/g, "").trim()}</td>
              <td>{item.match_name}</td>
              <td>{item.event_name || "-"}</td>
              <td>{item.winner_name}</td>
              <td className={`${Number(item.p_l_full) >= 0 ? "text-success" : "text-danger"}`}>
                {formatNumber(item.p_l_full)}
              </td>
              <td>{item.commission}</td>
              <td>{formatDateTime(String(item.settle_date_time))}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default FancyPLMarketWiseTableEventTable;
